import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Plus, Trash2, X, Save, ExternalLink, Eye, EyeOff } from "lucide-react";
import ImageUpload from "../../components/cms/ImageUpload";

export default function HomepageLogosAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/homepage-logos").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const blank = () => ({
    name: "", image_url: "", alt: "", title: "", url: "",
    order: (items.length || 0) + 1, active: true,
  });

  const save = async () => {
    setError("");
    try {
      if (editing.id) {
        const { id, ...body } = editing;
        await api.patch(`/admin/homepage-logos/${id}`, body);
      } else {
        await api.post("/admin/homepage-logos", editing);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este logo del carrusel?")) return;
    await api.delete(`/admin/homepage-logos/${id}`);
    load();
  };

  const toggle = async (item) => {
    const { id, ...body } = item;
    await api.patch(`/admin/homepage-logos/${id}`, { ...body, active: !item.active });
    load();
  };

  return (
    <div data-testid="cms-homepage-logos">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F2040]">Logos de la portada</h1>
          <p className="text-sm text-gray-600">
            Socios destacados que aparecen en el carrusel de la página principal. Controle los datos alt y title para SEO.
          </p>
        </div>
        <button
          onClick={() => setEditing(blank())}
          data-testid="hplogo-add"
          className="bg-[#C8102E] text-white px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[#A30D25]"
        >
          <Plus className="w-4 h-4" /> Añadir logo
        </button>
      </div>

      <div className="grid gap-3">
        {items.length === 0 ? (
          <div className="bg-white border border-gray-200 p-10 text-center text-gray-400">
            Aún no hay logos. Pulse "Añadir logo" para crear el primero.
          </div>
        ) : items.map((l) => (
          <div key={l.id} className="bg-white border border-gray-200 p-5 flex flex-wrap gap-5 items-center" data-testid={`hplogo-row-${l.id}`}>
            <div className="w-28 h-16 bg-gray-50 flex items-center justify-center border border-gray-100 p-2 shrink-0">
              {l.image_url ? (
                <img src={l.image_url} alt={l.alt || l.name} className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-xs text-gray-400">Sin imagen</span>
              )}
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 uppercase tracking-wider">#{l.order}</span>
                {l.active ? (
                  <span className="text-xs text-green-700">● Visible</span>
                ) : (
                  <span className="text-xs text-gray-400">○ Oculto</span>
                )}
              </div>
              <h3 className="font-medium text-[#0F2040]">{l.name}</h3>
              <p className="text-xs text-gray-500 mt-0.5 italic">alt: {l.alt || "—"}</p>
              <p className="text-xs text-gray-500 italic">title: {l.title || "—"}</p>
              {l.url && (
                <a href={l.url} target="_blank" rel="noreferrer" className="text-xs text-[#0077B5] hover:underline inline-flex items-center gap-1 mt-1">
                  {l.url} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggle(l)}
                className="px-3 py-2 text-xs border border-gray-200 hover:border-[#0F2040] inline-flex items-center gap-1.5"
                title={l.active ? "Ocultar" : "Mostrar"}
                data-testid={`hplogo-toggle-${l.id}`}
              >
                {l.active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {l.active ? "Ocultar" : "Mostrar"}
              </button>
              <button onClick={() => setEditing(l)} className="px-3 py-2 text-xs border border-gray-200 hover:border-[#0F2040]">
                Editar
              </button>
              <button onClick={() => remove(l.id)} className="p-2 text-gray-400 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="hplogo-editor">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-[#0F2040]">{editing.id ? "Editar logo" : "Nuevo logo del carrusel"}</h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <Field
                label="Nombre de la empresa *"
                value={editing.name}
                onChange={(v) => setEditing((prev) => ({ ...prev, name: v }))}
                placeholder="Aptiv Connection Systems Morocco"
              />
              <ImageUpload
                label="Logo del socio *"
                aspect="logo"
                value={editing.image_url}
                onChange={(v) => setEditing((prev) => ({ ...prev, image_url: v }))}
                help="Suba el archivo desde su ordenador. Formatos: JPG, PNG, WebP, GIF o SVG. Recomendado: fondo transparente."
              />
              <Field
                label="Alt (SEO y accesibilidad) *"
                value={editing.alt}
                onChange={(v) => setEditing((prev) => ({ ...prev, alt: v }))}
                placeholder="Logo Aptiv — socio de la CECIT"
                help="Texto alternativo que Google y los lectores de pantalla leerán. Debe describir la imagen."
              />
              <Field
                label="Title (tooltip al pasar el ratón)"
                value={editing.title}
                onChange={(v) => setEditing((prev) => ({ ...prev, title: v }))}
                placeholder="Aptiv Connection Systems Morocco — socio premium de la CECIT"
                help="Frase que aparece como tooltip. Buena práctica SEO para enriquecer el contexto."
              />
              <Field
                label="URL de destino (opcional)"
                value={editing.url}
                onChange={(v) => setEditing((prev) => ({ ...prev, url: v }))}
                placeholder="https://www.aptiv.com"
                help="Si se indica, al hacer clic en el logo se abrirá esta URL en una nueva pestaña."
              />
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Orden"
                  type="number"
                  value={editing.order}
                  onChange={(v) => setEditing((prev) => ({ ...prev, order: parseInt(v || "0") }))}
                />
                <label className="flex items-center gap-2 text-sm pt-6">
                  <input
                    type="checkbox"
                    checked={editing.active}
                    onChange={(e) => setEditing((prev) => ({ ...prev, active: e.target.checked }))}
                    className="accent-[#C8102E]"
                  />
                  Visible en la web
                </label>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50">Cancelar</button>
                <button
                  onClick={save}
                  data-testid="hplogo-save"
                  className="bg-[#0F2040] text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#1A2F5C]"
                >
                  <Save className="w-4 h-4" /> Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, help }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
      />
      {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
    </div>
  );
}
